export const MINIMO_BUSQUEDA_MERCADERIA = 1;
export const DEBOUNCE_BUSQUEDA_MERCADERIA_MS = 400;

export function normalizarFiltrosMercaderia(filtros = {}) {
  return {
    codigo: String(filtros.codigo ?? "").trim(),
    descripcion: String(filtros.descripcion ?? "").trim(),
  };
}

export function crearFiltrosMercaderiaDesdeTexto(texto = "") {
  const valor = String(texto).trim();
  return { codigo: valor, descripcion: valor };
}

export function crearConsultasMercaderiaDesdeTexto(texto = "") {
  const valor = String(texto).trim();
  if (!valor) return [];
  return [
    { codigo: valor, descripcion: "" },
    { codigo: "", descripcion: valor },
  ];
}

export async function buscarMercaderiasPorTexto(consultar, texto, signal) {
  const consultas = crearConsultasMercaderiaDesdeTexto(texto);
  const respuestas = await Promise.all(
    consultas.map((filtros) => consultar({ ...filtros, signal })),
  );
  const errores = respuestas.filter((respuesta) => respuesta?.error).map((respuesta) => respuesta.error);
  const listas = respuestas.filter(Array.isArray);

  if (listas.length === 0 && errores.length > 0) throw new Error(errores[0]);

  const unicos = new Map();
  listas.flat().forEach((item) => {
    const clave = item?.Id ?? `${item?.sCodigo ?? ""}\u0000${item?.sDescripcion ?? ""}`;
    if (!unicos.has(clave)) unicos.set(clave, item);
  });
  return [...unicos.values()];
}

export function evaluarFiltrosMercaderia(filtros = {}) {
  const normalizados = normalizarFiltrosMercaderia(filtros);
  const valores = [normalizados.codigo, normalizados.descripcion];
  const hayContenido = valores.some(Boolean);
  const camposValidos = valores.every(
    (valor) => !valor || valor.length >= MINIMO_BUSQUEDA_MERCADERIA,
  );

  return {
    filtros: normalizados,
    valido: hayContenido && camposValidos,
    vacio: !hayContenido,
    clave: `${normalizados.codigo}\u0000${normalizados.descripcion}`,
  };
}

export function crearRequestMercaderiasPiSeun(EmpresaId, iFiltro, filtros = {}) {
  const { filtros: normalizados } = evaluarFiltrosMercaderia(filtros);
  return {
    EmpresaId,
    iFiltro,
    codigo: normalizados.codigo,
    descripcion: normalizados.descripcion,
    ...((filtros.ListarTodos ?? filtros.listarTodos) ? { ListarTodos: true } : {}),
  };
}

export function formatearMercaderia(mercaderia = {}) {
  const codigo = String(mercaderia.sCodigo ?? "").trim();
  const descripcion = String(mercaderia.sDescripcion ?? "").trim();
  const stock = String(mercaderia.sExtra ?? "0").trim() || "0";
  const unidad = String(mercaderia.sExtra2 ?? "").trim();
  const identidad = [codigo, descripcion].filter(Boolean).join(" - ");
  return `${identidad} | Stock: ${stock}${unidad ? ` ${unidad}` : ""}`;
}

export function crearControladorBusquedaMercaderias({
  consultar,
  alLimpiar = () => {},
  alBuscar = () => {},
  alResolver = () => {},
  alError = () => {},
  debounceMs = DEBOUNCE_BUSQUEDA_MERCADERIA_MS,
}) {
  let temporizador = null;
  let controlador = null;
  let secuencia = 0;
  let ultimaClave = null;

  const cancelarPendiente = () => {
    if (temporizador) clearTimeout(temporizador);
    temporizador = null;
    controlador?.abort();
    controlador = null;
  };

  const actualizar = (filtros, { forzar = false } = {}) => {
    const evaluacion = evaluarFiltrosMercaderia(filtros);

    if (!forzar && evaluacion.valido && evaluacion.clave === ultimaClave) return;

    cancelarPendiente();
    secuencia += 1;
    const miSecuencia = secuencia;

    if (!evaluacion.valido) {
      ultimaClave = null;
      alLimpiar(evaluacion);
      return;
    }

    temporizador = setTimeout(async () => {
      temporizador = null;
      ultimaClave = evaluacion.clave;
      controlador = new AbortController();
      const controladorActual = controlador;
      alBuscar(evaluacion);

      try {
        const resultado = await consultar(evaluacion.filtros, controladorActual.signal);
        if (miSecuencia === secuencia && !controladorActual.signal.aborted) {
          alResolver(resultado, evaluacion);
        }
      } catch (error) {
        if (miSecuencia === secuencia && error?.name !== "AbortError") {
          ultimaClave = null;
          alError(error, evaluacion);
        }
      } finally {
        if (miSecuencia === secuencia) controlador = null;
      }
    }, debounceMs);
  };

  const destruir = () => {
    secuencia += 1;
    cancelarPendiente();
  };

  return { actualizar, destruir };
}
